import { useState } from 'react';
import { Button } from '../design-system/Button';
import { Check, Modal } from '../design-system';
import type { IframePoolOrderItem } from '../api';
import { BuybackRippleWaves } from './BuybackRippleWaves';
import { writeSkipBuybackConfirm } from './skipBuybackConfirm';

export interface BuybackConfirmModalProps {
  item: IframePoolOrderItem;
  offer: NonNullable<IframePoolOrderItem['bestActiveOffer']>;
  /** Sell-only path. Fires when the user clicks "Accept Offer". */
  onAccept: () => void;
  /** Sell + immediately buy another of the same pack. Fires when
   *  the user clicks "Accept Offer & Rip Again". */
  onAcceptAndRipAgain: () => void;
  /** Close without selling — X button or backdrop click. */
  onClose: () => void;
}

/**
 * Confirmation modal shown before the buyback fires (Figma
 * 881:930). The user can either accept-only, accept-and-buy-
 * another, or cancel via the X. The "Don't show this again"
 * checkbox latches the choice in `localStorage`; when set, the
 * Buyback button bypasses this modal on future opens.
 */
export function BuybackConfirmModal({
  item,
  offer,
  onAccept,
  onAcceptAndRipAgain,
  onClose,
}: BuybackConfirmModalProps) {
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const fmv = item.fairMarketValue ?? offer.grossPrice;
  // Figma 702:15719 prints the buyback ratio as a header — "Buyback 90%"
  // — rather than the static "Buyback Offer:" we had before. `buybackRate`
  // can arrive as a fraction (0.90) or already a percentage (90); the
  // ternary normalises both. Falls back to the generic label when the
  // offer doesn't carry the rate.
  const offerLabel = (() => {
    const rate = offer.buybackRate;
    if (rate == null || !Number.isFinite(rate) || rate <= 0) {
      return 'Buyback Offer:';
    }
    const pct = rate > 1 ? rate : rate * 100;
    return `Buyback ${Math.round(pct)}%`;
  })();

  // Wrap the action handlers so both persist the chosen action
  // (when the user ticked "Don't show this again") before invoking
  // the parent. Storing the specific action — not just "skip" —
  // lets WinnerStep replay exactly what the user picked last time.
  const handleAccept = () => {
    if (dontShowAgain) writeSkipBuybackConfirm('accept');
    onAccept();
  };
  const handleAcceptAndRip = () => {
    if (dontShowAgain) writeSkipBuybackConfirm('acceptAndRip');
    onAcceptAndRipAgain();
  };

  return (
    <Modal
      onClose={onClose}
      inspectName="BuybackConfirmModal"
      ariaLabelledBy="buyback-confirm-title"
    >
      {/* Card stack — flex column for X close + outer-white-card +
          actions. `Modal` provides positioning + backdrop only; the
          inner layout (spacing, alignment) stays a per-modal concern. */}
      <div className="flex flex-col items-stretch gap-6">
          {/* Close (X) — responsive position:
              • Mobile  (Figma 702:15719) → fixed in the top-right
                corner of the VIEWPORT (`fixed top-4 right-4`),
                lifted to `z-[60]` so it sits above the Modal
                backdrop (z-50).
              • ≥640px  (Figma 881:930)  → absolutely positioned at
                the top-right corner of the dialog card itself,
                half-overlapping the corner via translate(50%,-50%).
              `sm:` cascades override the mobile defaults; the order
              matters — `sm:absolute` wins over `fixed`. */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel"
            className="
              fixed top-4 right-4 z-[60]
              sm:absolute sm:top-0 sm:right-0 sm:z-10
              sm:translate-x-full sm:-translate-y-1/2
              flex h-10 w-10 items-center justify-center rounded-full
              bg-black/60 text-white text-[20px] leading-none
              hover:bg-black/75 transition-colors
            "
          >
            ×
          </button>

          {/* Outer white card — Figma 881:930. The card itself has NO
              border; the green ring lives on the inner card. Holds
              the FMV/Buyback Offer block + the "Don't show this
              again" checkbox below it. Theme-able via
              `--co-BuybackConfirmModal-cardFill`. */}
          <div
            id="buyback-confirm-title"
            className="relative rounded-[24px] p-1 pb-5"
            style={{
              backgroundColor:
                'var(--co-BuybackConfirmModal-cardFill, #FFFFFF)',
            }}
          >
            {/* Inner card — green-bordered, holds the two value rows.
                Border colour comes from `--co-BuybackConfirmModal-cardBorder`.
                Background stays transparent so the outer card's fill
                shows through, matching Figma. */}
            <div
              className="rounded-[24px] border-2 overflow-hidden"
              style={{
                borderColor:
                  'var(--co-BuybackConfirmModal-cardBorder, var(--ui-accent, #22E11C))',
              }}
            >
              {/* FMV row — Figma 702:15719 paints the top strip of the
                  inner card with a very pale green wash. Themed via
                  `--co-BuybackConfirmModal-fmvRowBg` so the partner can
                  swap the tint or kill it. */}
              <div
                className="flex justify-between items-baseline text-[14px] leading-[20px] px-4 py-3"
                style={{
                  backgroundColor:
                    'var(--co-BuybackConfirmModal-fmvRowBg, rgba(34,225,28,0.06))',
                }}
              >
                <span
                  style={{
                    color:
                      'var(--co-BuybackConfirmModal-fmvLabelColor, rgba(0,0,0,0.55))',
                  }}
                >
                  Fair Market Value:
                </span>
                <span
                  className="font-medium"
                  style={{
                    color:
                      'var(--co-BuybackConfirmModal-fmvValueColor, rgba(0,0,0,0.70))',
                  }}
                >
                  ${fmv.toFixed(2)}
                </span>
              </div>

              {/* Offer block — Figma 702:15719. Label is "Buyback {rate}%"
                  when we have a buyback_rate from the offer, otherwise
                  the generic "Buyback Offer:" fallback. */}
              <div className="flex flex-col items-center px-4 pt-4 pb-6 gap-1">
                <span
                  className="text-[15px] leading-[22px]"
                  style={{
                    color:
                      'var(--co-BuybackConfirmModal-offerLabelColor, rgba(0,0,0,0.55))',
                  }}
                >
                  {offerLabel}
                </span>
                <span
                  className="text-[40px] leading-[48px] font-bold tracking-[-0.6px]"
                  style={{
                    color:
                      'var(--co-BuybackConfirmModal-offerValueColor, #15181E)',
                  }}
                >
                  ${offer.grossPrice.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Checkbox — "Don't show this again". Lives under the
                inner green card but inside the outer white card. */}
            <label className="mt-4 flex items-center justify-center gap-2.5 cursor-pointer select-none">
              <span
                className="
                  relative flex h-5 w-5 shrink-0 items-center justify-center
                  rounded-[6px] border
                "
                style={{
                  backgroundColor: dontShowAgain
                    ? 'var(--co-BuybackConfirmModal-checkBoxFill, var(--ui-accent, #22E11C))'
                    : 'transparent',
                  borderColor: dontShowAgain
                    ? 'var(--co-BuybackConfirmModal-checkBoxBorder, var(--ui-accent, #22E11C))'
                    : 'rgba(0,0,0,0.30)',
                }}
              >
                {dontShowAgain && (
                  <Check
                    size={14}
                    strokeWidth={3}
                    style={{ color: 'var(--co-BuybackConfirmModal-checkColor, #FFFFFF)' }}
                  />
                )}
              </span>
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="sr-only"
              />
              <span
                className="text-[15px] leading-[22px]"
                style={{
                  color:
                    'var(--co-BuybackConfirmModal-checkboxTextColor, #15181E)',
                }}
              >
                Don’t show it again
              </span>
            </label>
          </div>

          {/* Actions — two CTAs stacked:
                • Primary  "Accept Offer & Rip Again" — sell AND
                  immediately buy another pack of the same offer.
                  Wrapped in a `relative` host so BuybackRippleWaves
                  sits absolutely behind it; Button bumped to `z-10`
                  so the rings render BEHIND, not over the label.
                • Secondary "Accept Offer" — plain sell-only, closes
                  reveal and returns to the pack page.
              Themed via `Button → primary` / `Button → secondary`
              with no per-modal overrides. */}
          <div className="flex flex-col items-stretch gap-3 pt-2">
            <div className="relative w-full">
              <BuybackRippleWaves />
              <Button
                variant="primary"
                onClick={handleAcceptAndRip}
                fullWidth
                className="relative z-10 h-14"
              >
                Accept Offer &amp; Rip Again
              </Button>
            </div>
            <Button
              variant="accent-gloss"
              onClick={handleAccept}
              fullWidth
            >
              Accept Offer
            </Button>
          </div>
      </div>
    </Modal>
  );
}
